import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Loader2, PanelLeftClose, PanelLeft } from "lucide-react";
import { Button, cn } from "@tellescope/ui";
import { PaginatedList, Tooltip } from "@tellescope/design-system";
import { formatDate } from "@tellescope/utils";
import { useConversationList } from "../hooks";
import type { ConversationPreview } from "../types";

interface ConversationListProps {
  onSelect: (conversationId: string) => void;
  onNewConversation: () => void;
  selectedId: string | null;
  isNewConversation: boolean;
}

export const ConversationList = ({
  onSelect,
  onNewConversation,
  selectedId,
  isNewConversation,
}: ConversationListProps) => {
  const { t } = useTranslation("ai");
  const { conversations, isLoading, loadMore, hasMore } = useConversationList();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const hasConversations = conversations.length > 0;

  // Show new conversation button when not on new conversation screen
  const showNewConversationButton = !isNewConversation;

  // Hide sidebar only when: no conversations AND on new conversation screen (first-time user)
  if (!hasConversations && !isLoading && isNewConversation) {
    return null;
  }

  // Collapsed view
  if (isCollapsed) {
    return (
      <div className="w-[52px] border-r min-h-full flex flex-col self-stretch">
        <div className="px-[10px] py-2 flex flex-col gap-2">
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Button onClick={() => setIsCollapsed(false)} variant="ghost" size="icon" className="h-8 w-8">
                <PanelLeft className="h-4 w-4" />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content side="right">{t("Chat history")}</Tooltip.Content>
          </Tooltip>
          {showNewConversationButton && (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <Button onClick={onNewConversation} variant="ghost" size="icon" className="h-8 w-8">
                  <Plus className="h-4 w-4" />
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content side="right">{t("New conversation")}</Tooltip.Content>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="w-64 border-r min-h-full flex flex-col self-stretch">
      <div className="px-3 py-2 flex items-center gap-2">
        {showNewConversationButton ? (
          <Button onClick={onNewConversation} className="flex-1" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            {t("New conversation")}
          </Button>
        ) : (
          <span className="flex-1 text-sm font-medium">{t("Chat history")}</span>
        )}
        <Tooltip>
          <Tooltip.Trigger asChild>
            <Button onClick={() => setIsCollapsed(true)} variant="ghost" size="icon" className="h-8 w-8 shrink-0">
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content side="right">{t("Collapse sidebar")}</Tooltip.Content>
        </Tooltip>
      </div>
      {isLoading && conversations.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <PaginatedList className="flex-1" onBottomReached={() => hasMore && loadMore()}>
          {conversations.map((conv) => (
            <ConversationListItem
              key={conv.id}
              conversation={conv}
              isSelected={selectedId === conv.id}
              onSelect={onSelect}
            />
          ))}
        </PaginatedList>
      )}
    </div>
  );
};

const ConversationListItem = ({
  conversation,
  isSelected,
  onSelect,
}: {
  conversation: ConversationPreview;
  isSelected: boolean;
  onSelect: (id: string) => void;
}) => (
  <button
    onClick={() => onSelect(conversation.id)}
    className={cn(
      "w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors rounded-sm mx-1",
      isSelected && "bg-muted"
    )}
  >
    <p className="truncate text-sm">{conversation.preview}</p>
    <p className="text-xs text-muted-foreground mt-0.5">
      {formatDate(conversation.updatedAt, "MMM D, YYYY", "always")}
    </p>
  </button>
);
